import React, {useEffect, useState} from "react";
import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid} from "@mui/material";
import {toast} from "react-toastify";
import {CampaignRepository} from "../../../../../repositories/CampaignRepository";
import {buildCampaignBody, campaignToForm, validateCampaignForm} from "../../utils";
import {usePhoneNumbers} from "../../hooks/usePhoneNumbers";
import {useAmiExtensions} from "../../hooks/useAmiExtensions";
import {CampaignDetailsFields} from "./CampaignDetailsFields";
import {CampaignCallFields} from "./CampaignCallFields";
import {CampaignScheduleFields} from "./CampaignScheduleFields";
import {PhoneNumbersInput} from "./PhoneNumbersInput";
import {PhoneNumbersTable} from "./PhoneNumbersTable";

const addResultMessage = (result) => {
  const skipped = (result?.alreadyInCampaign || 0) + (result?.duplicatesInRequest || 0) + (result?.empty || 0);
  return `Added ${result?.added ?? 0} phone number(s)` + (skipped ? `, skipped ${skipped} already in the campaign` : "");
};

// Creates a campaign, or edits `campaign` when it's given
export const CampaignFormDialog = ({open, onClose, onSaved, flowUid, campaign = null}) => {
  const isEdit = !!campaign;
  const [form, setForm] = useState(campaignToForm(campaign));
  const [saving, setSaving] = useState(false);
  const numbers = usePhoneNumbers();
  const amiExtensions = useAmiExtensions(open ? flowUid : null);

  useEffect(() => {
    if (open) {
      setForm(campaignToForm(campaign));
      numbers.clear();
    }
  }, [open, campaign?.id]);

  const setField = (field) => (e) => setForm(prev => ({...prev, [field]: e.target.value}));

  const {errors, isValid} = validateCampaignForm(form);
  const canSubmit = !saving && isValid && numbers.invalidCount === 0;

  const create = async () => {
    const response = await CampaignRepository.create(buildCampaignBody(form, numbers.phoneNumbers, flowUid));
    toast.success("Campaign created");
    return response?.data;
  };

  // Existing numbers are left untouched (phoneNumbers: null); new ones are added separately
  const update = async () => {
    const response = await CampaignRepository.update(campaign.id, buildCampaignBody(form, null, flowUid));
    if (numbers.phoneNumbers.length > 0) {
      const added = await CampaignRepository.addPhoneNumbers(campaign.id, numbers.phoneNumbers.map(row => row.value.trim()));
      toast.info(addResultMessage(added?.data));
      const refreshed = await CampaignRepository.get(campaign.id);
      toast.success("Campaign updated");
      return refreshed?.data;
    }
    toast.success("Campaign updated");
    return response?.data;
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const saved = isEdit ? await update() : await create();
      onSaved?.(saved);
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || `Failed to ${isEdit ? "update" : "create"} campaign`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? "Edit Campaign" : "Create Campaign"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{pt: 1}}>
          <CampaignDetailsFields form={form} errors={errors} setField={setField}/>
          <CampaignCallFields form={form} setField={setField} amiExtensions={amiExtensions}/>
          <CampaignScheduleFields form={form} errors={errors} setField={setField}/>
          {isEdit &&
            <Grid item xs={12}>
              <Alert severity="info" sx={{py: 0}}>
                The campaign's current {campaign.totalPhoneNumbers ?? 0} phone number(s) are kept. Numbers you add
                here are added to them; numbers already in the campaign are skipped. Remove numbers from the
                campaign page.
              </Alert>
            </Grid>
          }
          <Grid item xs={12}>
            <PhoneNumbersInput onAdd={numbers.addNumbers} label={isEdit ? "Add phone numbers" : "Phone numbers"}/>
          </Grid>
          <Grid item xs={12}>
            <PhoneNumbersTable numbers={numbers}/>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{px: 3, pb: 3}}>
        <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="modal" onClick={handleSubmit} disabled={!canSubmit}>
          {isEdit ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
